import { normalizeGridTuning, type GridTuning } from "@/character/draft";
import type { CharacterGridSpec } from "@/character/grid";
import { normalizeGridSpec } from "@/character/grid";
import {
  isStorageQuotaError,
  loadGmMonsterCatalogFromIdb,
  saveGmMonsterCatalogToIdb,
} from "@/gm/gm-monster-catalog-idb";
import { getGameSettings } from "@/game/model/game-settings";
import {
  createPartDefinition,
  type HomePartDefinition,
  type HomeRoomData,
} from "@/home/storage";

export const GM_MONSTER_CATALOG_VERSION = 2 as const;

export type GmMonsterTemplate = {
  version: typeof GM_MONSTER_CATALOG_VERSION;
  id: string;
  name: string;
  imageDataUrl: string;
  cellsW: number;
  cellsH: number;
  monsterLevel: number;
  monsterBaseHp: number;
  monsterBaseAttack: number;
  monsterBaseDefense: number;
  monsterBaseDodge: number;
  monsterXpReward: number;
  spritesheetDataUrl?: string;
  grid?: CharacterGridSpec;
  /** Session-only re-edit; not persisted (saves quota). */
  sourceSheetDataUrl?: string;
  gridTuning?: GridTuning;
  createdAt: number;
  updatedAt: number;
};

const LEGACY_LOCAL_KEY = "mind-survival/gm-monster-catalog/v1";

export const GM_MONSTER_PLACEHOLDER_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect fill="#7c2d2d" width="64" height="64" rx="10"/><circle cx="32" cy="28" r="14" fill="#ef9a9a"/><text x="32" y="54" text-anchor="middle" fill="#ffe0e0" font-family="system-ui,sans-serif" font-size="11">MON</text></svg>`,
  );

let catalogCache: GmMonsterTemplate[] | null = null;
let hydratePromise: Promise<void> | null = null;

function newMonsterId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `gm-monster-${crypto.randomUUID()}`;
  }
  return `gm-monster-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function normalizeCatalogGridTuning(raw: unknown): GridTuning | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const g = raw as Partial<GridTuning> & { offset?: Partial<GridTuning["offset"]> };
  return normalizeGridTuning({
    lockTemplateGrid: Boolean(g.lockTemplateGrid),
    frameSize:
      typeof g.frameSize === "number" && Number.isFinite(g.frameSize)
        ? Math.round(g.frameSize)
        : null,
    offset: {
      x: typeof g.offset?.x === "number" ? g.offset.x : 0,
      y: typeof g.offset?.y === "number" ? g.offset.y : 0,
    },
    rowSplit: Array.isArray(g.rowSplit) ? g.rowSplit.map(Boolean) : undefined,
    rowOffsetY: Array.isArray(g.rowOffsetY)
      ? g.rowOffsetY.map((v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0))
      : undefined,
    rowOffsetX: Array.isArray(g.rowOffsetX)
      ? g.rowOffsetX.map((v) => (typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0))
      : undefined,
  });
}

export function normalizeGmMonsterTemplate(raw: unknown): GmMonsterTemplate | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<GmMonsterTemplate>;
  if (typeof p.id !== "string" || !p.id || typeof p.name !== "string" || !p.name) return null;
  const d = getGameSettings();
  const now = Date.now();
  return {
    version: GM_MONSTER_CATALOG_VERSION,
    id: p.id,
    name: p.name.trim().slice(0, 80),
    imageDataUrl:
      typeof p.imageDataUrl === "string" && p.imageDataUrl.startsWith("data:")
        ? p.imageDataUrl
        : GM_MONSTER_PLACEHOLDER_IMAGE,
    cellsW: clampInt(p.cellsW, 1, 4, 1),
    cellsH: clampInt(p.cellsH, 1, 4, 1),
    monsterLevel: clampInt(p.monsterLevel, 1, 100, d.monsterDefaultLevel),
    monsterBaseHp: clampInt(p.monsterBaseHp, 1, 1_000_000, d.monsterDefaultHp),
    monsterBaseAttack: clampInt(p.monsterBaseAttack, 1, 100_000, d.monsterDefaultAttack),
    monsterBaseDefense: clampInt(p.monsterBaseDefense, 0, 100_000, d.monsterDefaultDefense),
    monsterBaseDodge: clampInt(p.monsterBaseDodge, 0, 100_000, d.monsterDefaultDodge),
    monsterXpReward: clampInt(p.monsterXpReward, 0, 10_000_000, d.monsterDefaultXpReward),
    spritesheetDataUrl:
      typeof p.spritesheetDataUrl === "string" && p.spritesheetDataUrl.startsWith("data:")
        ? p.spritesheetDataUrl
        : undefined,
    grid: p.grid ? normalizeGridSpec(p.grid as CharacterGridSpec) : undefined,
    gridTuning: normalizeCatalogGridTuning(p.gridTuning),
    createdAt: typeof p.createdAt === "number" ? p.createdAt : now,
    updatedAt: typeof p.updatedAt === "number" ? p.updatedAt : now,
  };
}

/** Drop full upload duplicate — keep cropped sheet + idle thumb only. */
function stripForPersist(entry: GmMonsterTemplate): GmMonsterTemplate {
  const { sourceSheetDataUrl: _drop, ...rest } = entry;
  return rest;
}

function parseLegacyLocalCatalog(): GmMonsterTemplate[] {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => normalizeGmMonsterTemplate(item))
      .filter((item): item is GmMonsterTemplate => item !== null)
      .map(stripForPersist);
  } catch {
    localStorage.removeItem(LEGACY_LOCAL_KEY);
    return [];
  }
}

async function persistCatalog(items: GmMonsterTemplate[]): Promise<void> {
  const payload = items.map(stripForPersist);
  await saveGmMonsterCatalogToIdb(payload);
  catalogCache = payload;
}

export async function hydrateGmMonsterCatalog(): Promise<void> {
  if (catalogCache) return;
  if (hydratePromise) {
    await hydratePromise;
    return;
  }
  hydratePromise = (async () => {
    let items = await loadGmMonsterCatalogFromIdb();
    if (!items?.length) {
      const legacy = parseLegacyLocalCatalog();
      if (legacy.length > 0) {
        items = legacy;
        try {
          await saveGmMonsterCatalogToIdb(legacy);
          localStorage.removeItem(LEGACY_LOCAL_KEY);
        } catch {
          catalogCache = legacy;
          return;
        }
      } else {
        items = [];
      }
    }
    catalogCache = items
      .map((item) => normalizeGmMonsterTemplate(item))
      .filter((item): item is GmMonsterTemplate => item !== null);
  })();
  await hydratePromise;
}

export function loadGmMonsterCatalog(): GmMonsterTemplate[] {
  return catalogCache ?? [];
}

export async function upsertGmMonsterCatalog(entry: GmMonsterTemplate): Promise<void> {
  await hydrateGmMonsterCatalog();
  const list = [...loadGmMonsterCatalog()];
  const next = stripForPersist({ ...entry, updatedAt: Date.now() });
  const idx = list.findIndex((m) => m.id === next.id);
  if (idx >= 0) list[idx] = next;
  else list.push(next);
  await persistCatalog(list);
}

export async function deleteGmMonsterFromCatalog(id: string): Promise<void> {
  await hydrateGmMonsterCatalog();
  await persistCatalog(loadGmMonsterCatalog().filter((m) => m.id !== id));
}

export function createGmMonsterTemplate(
  input: Omit<GmMonsterTemplate, "version" | "id" | "createdAt" | "updatedAt"> & {
    id?: string;
    createdAt?: number;
  },
): GmMonsterTemplate {
  const now = Date.now();
  const base = normalizeGmMonsterTemplate({
    ...input,
    id: input.id ?? newMonsterId(),
    createdAt: input.createdAt ?? now,
    updatedAt: now,
  });
  if (!base) throw new Error("invalid monster template");
  return base;
}

export function gmMonsterTemplateToPart(template: GmMonsterTemplate): HomePartDefinition {
  return createPartDefinition({
    id: template.id,
    name: template.name,
    imageDataUrl: template.imageDataUrl,
    cellsW: template.cellsW,
    cellsH: template.cellsH,
    kind: "monster",
    createdAt: template.createdAt,
    monsterLevel: template.monsterLevel,
    monsterBaseHp: template.monsterBaseHp,
    monsterBaseAttack: template.monsterBaseAttack,
    monsterBaseDefense: template.monsterBaseDefense,
    monsterBaseDodge: template.monsterBaseDodge,
    monsterXpReward: template.monsterXpReward,
    spritesheetDataUrl: template.spritesheetDataUrl,
    grid: template.grid,
    gridTuning: template.gridTuning,
  });
}

/** Sync global GM monsters into a home room catalog (by part id). */
export function mergeGmMonsterCatalogIntoHome(home: HomeRoomData): HomeRoomData {
  const templates = loadGmMonsterCatalog();
  if (templates.length === 0) return home;
  const parts = [...home.parts];
  for (const template of templates) {
    const part = gmMonsterTemplateToPart(template);
    const idx = parts.findIndex((p) => p.id === part.id);
    if (idx >= 0) parts[idx] = part;
    else parts.push(part);
  }
  return { ...home, parts };
}

export function defaultNewMonsterForm(): Omit<
  GmMonsterTemplate,
  "version" | "id" | "createdAt" | "updatedAt"
> {
  const d = getGameSettings();
  return {
    name: "มอนสเตอร์ใหม่",
    imageDataUrl: GM_MONSTER_PLACEHOLDER_IMAGE,
    cellsW: 1,
    cellsH: 1,
    monsterLevel: d.monsterDefaultLevel,
    monsterBaseHp: d.monsterDefaultHp,
    monsterBaseAttack: d.monsterDefaultAttack,
    monsterBaseDefense: d.monsterDefaultDefense,
    monsterBaseDodge: d.monsterDefaultDodge,
    monsterXpReward: d.monsterDefaultXpReward,
  };
}

export { isStorageQuotaError };
